import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Tenant, AccessibleTenant } from '@/types'
import type { ModuleConfig } from '@/types/modules'
import { queryClient } from '@/lib/queryClient'
import { isSuperAdmin } from '@/lib/role'
import { onAuthMessage, postAuthMessage } from '@/stores/authChannel'

/** Where the persisted session lives. refreshSession reads the same record to
 * pick up a pair another tab rotated, so the key has one home. */
export const AUTH_STORAGE_KEY = 'auth-storage'

// Cross-tab session sync: lets new tabs inherit the session-alive signal
// so "no remember me" still works across tabs in the same browser session.
onAuthMessage((message) => {
  if (message === 'alive') {
    sessionStorage.setItem('auth-session-alive', 'true')
  } else if (message === 'dead') {
    sessionStorage.removeItem('auth-session-alive')
  } else if (message === 'query' && sessionStorage.getItem('auth-session-alive')) {
    postAuthMessage('alive')
  }
})

/** The password step succeeded but a TOTP step stands between the user and
 * a session. `setup` means the tenant requires MFA and the user must enroll
 * first; `code` means they are enrolled and must answer the challenge. */
export interface MfaChallenge {
  kind: 'code' | 'setup'
  mfaToken: string
}

interface AuthState {
  user: User | null
  tenant: Tenant | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  hasHydrated: boolean
  activeTenantId: string | null
  /** Tenants this user may switch between ("one login, multiple companies"). */
  tenants: AccessibleTenant[]
  /** Resolves to an MfaChallenge instead of committing a session when the
   * backend demands a second factor. */
  login: (email: string, password: string, rememberMe?: boolean) => Promise<MfaChallenge | void>
  /** Answer the TOTP challenge (or MFA-setup confirm) and commit the session. */
  completeMfaLogin: (path: string, body: Record<string, string>, rememberMe?: boolean) => Promise<void>
  logout: () => void
  setToken: (token: string) => void
  /** Store the pair a refresh returns. Refresh-token rotation retires the old
   * refresh token, so keeping only the access token logs the user out at the
   * next refresh. */
  setTokens: (token: string, refreshToken: string | null) => void
  setActiveTenantId: (id: string | null) => void
  setTenants: (tenants: AccessibleTenant[]) => void
  /** Switch active tenant AND update the user's role to that tenant's role.
   * Returns false (no-op) when the tenant is not among the user's memberships,
   * so the caller can surface the failure instead of silently proceeding.
   * Callers must invalidate queries so tenant-scoped data + module_config refetch
   * under the new X-Tenant-ID header. */
  switchTenant: (tenantId: string) => boolean
  updateModuleConfig: (config: ModuleConfig) => void
}

/** What the login/MFA endpoints return: either a session (tokens + user)
 * or an MFA challenge (mfa_* fields set, no tokens). */
interface AuthResponse {
  access: string
  refresh?: string | null
  user: User
  tenant: Tenant | null
  tenants?: AccessibleTenant[]
  mfa_required?: boolean
  mfa_setup_required?: boolean
  mfa_token?: string
}

/** POST to an auth endpoint; throws the backend's own error message. */
async function postAuth(path: string, body: Record<string, string>): Promise<AuthResponse> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    let errorMessage: string
    try {
      const error = await response.json()
      errorMessage = error.error?.message || error.detail || error.message || 'Login failed'
    } catch {
      errorMessage = `Login failed (HTTP ${response.status})`
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

/** Commit a successful session payload (tokens + user + tenants) to the store. */
function commitSession(
  data: AuthResponse,
  rememberMe: boolean,
  set: (partial: Partial<AuthState>) => void,
): void {
  localStorage.setItem('auth-remember-me', rememberMe ? 'true' : 'false')
  if (!rememberMe) {
    sessionStorage.setItem('auth-session-alive', 'true')
    postAuthMessage('alive')
  }
  set({
    user: data.user,
    tenant: data.tenant,
    tenants: data.tenants ?? [],
    token: data.access,
    refreshToken: data.refresh ?? null,
    isAuthenticated: true,
    isLoading: false,
    activeTenantId: data.user.role !== 'super_admin' && data.tenant?.id
      ? data.tenant.id
      : null,
  })
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      hasHydrated: false,
      activeTenantId: null,
      tenants: [],

      login: async (email: string, password: string, rememberMe = false) => {
        set({ isLoading: true })
        try {
          const data = await postAuth('/api/v1/auth/login/', { email, password })
          if (data.mfa_required || data.mfa_setup_required) {
            set({ isLoading: false })
            return {
              kind: data.mfa_required ? 'code' : 'setup',
              mfaToken: data.mfa_token ?? '',
            } satisfies MfaChallenge
          }
          commitSession(data, rememberMe, set)
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      completeMfaLogin: async (path: string, body: Record<string, string>, rememberMe = false) => {
        set({ isLoading: true })
        try {
          const data = await postAuth(path, body)
          commitSession(data, rememberMe, set)
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: () => {
        queryClient.clear()
        localStorage.removeItem('campaign-migration-dismissed')
        localStorage.removeItem('ar-contacts-column-visibility')
        localStorage.removeItem('auth-remember-me')
        sessionStorage.removeItem('auth-session-alive')
        postAuthMessage('dead')
        set({
          user: null,
          tenant: null,
          tenants: [],
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          activeTenantId: null,
        })
      },

      setToken: (token) => set({ token }),

      setTokens: (token, refreshToken) =>
        set(refreshToken ? { token, refreshToken } : { token }),

      setActiveTenantId: (id) => set({ activeTenantId: id }),
      setTenants: (tenants) => set({ tenants }),
      switchTenant: (tenantId) => {
        const membership = useAuthStore.getState().tenants.find((t) => t.id === tenantId)
        if (!membership) {
          // Not a tenant this user can access — do NOT silently no-op (that left
          // the session on the previous brand while the UI looked switched).
          console.warn(`[auth] switchTenant ignored: no membership for tenant ${tenantId}`)
          return false
        }
        set((state) => ({
          activeTenantId: tenantId,
          // Role is per-tenant: update so menu/role gating reflects the new tenant.
          user: state.user ? { ...state.user, role: membership.role } : state.user,
        }))
        return true
      },
      updateModuleConfig: (config) => set((state) => {
        // Refetches fire on every window focus — skip the update when nothing
        // changed so we don't mint a new `user` object and re-render every
        // subscriber for an identical config.
        if (!state.user || JSON.stringify(state.user.module_config) === JSON.stringify(config)) {
          return state
        }
        return { user: { ...state.user, module_config: config } }
      }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        // A super admin's tenant view is derived from the URL (TenantLayout),
        // never from storage — persisting it left them stuck in tenant view
        // (tenant sidebar/dashboard at super-admin URLs) after a reload.
        // Members keep it so a multi-company switch survives reload.
        activeTenantId: isSuperAdmin(state.user?.role) ? null : state.activeTenantId,
        tenants: state.tenants,
      }),
      onRehydrateStorage: () => {
        return (state, _error) => {
          // A super admin session always starts in super-admin view; only the
          // URL (TenantLayout) may scope them into a tenant. Also migrates
          // entries persisted before activeTenantId was excluded for them.
          if (state?.activeTenantId && isSuperAdmin(state.user?.role)) {
            useAuthStore.setState({ activeTenantId: null })
          }

          // If user appears authenticated but has no refresh token (old session before
          // refresh token flow was added), force logout so they get a fresh session.
          if (state?.isAuthenticated && !state.refreshToken) {
            useAuthStore.getState().logout()
            useAuthStore.setState({ hasHydrated: true })
            return
          }

          const rememberMe = localStorage.getItem('auth-remember-me')
          // react-doctor-disable-next-line react-doctor/js-cache-storage -- the later read at the 150ms-timeout MUST re-read sessionStorage fresh to detect a cross-tab reply, so these reads cannot be collapsed
          const sessionAlive = sessionStorage.getItem('auth-session-alive')

          if (rememberMe === 'false' && !sessionAlive) {
            // New tab or new browser session — ask other tabs if a session is alive.
            // Give them 150ms to reply before deciding to log out.
            postAuthMessage('query')
            setTimeout(() => {
              if (!sessionStorage.getItem('auth-session-alive')) {
                // No other tab confirmed a live session → this is a new browser session
                useAuthStore.getState().logout()
              }
              useAuthStore.setState({ hasHydrated: true })
            }, 150)
          } else {
            if (rememberMe === 'false' && sessionAlive) {
              // Refresh in the same tab — keep the marker alive
              sessionStorage.setItem('auth-session-alive', 'true')
            }
            queueMicrotask(() => {
              useAuthStore.setState({ hasHydrated: true })
            })
          }
        }
      },
    }
  )
)
